import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { CircularProgress } from '@mui/material'
import { motion } from 'framer-motion'
import moment from 'moment'

import { get, URL_VARIABLES } from 'services/api'
import { fetchQuestions, submitAnswers, getBonus } from 'redux/reducers/quiz'
import { OnboardTitle, CountdownTimer } from 'components/Antifraude'
import { addKeyToObject } from 'utils/addUniqueKeyToObjects'
import validateStageOnboarding from 'utils/validateStage'
import { roles } from 'utils/constants/roles'

import Container from './styles'

export default (props) => {
  const { history } = props
  const { token } = useSelector((state) => state.token)
  const quiz = useSelector((state) => state.quiz)
  const dispatch = useDispatch()

  const [questionIndex, setQuestionIndex] = useState(0)
  const [currentQuestion, setCurrentQuestion] = useState(null)
  const [showQuestion, setShowQuestion] = useState(false)
  const [loadingStage, setLoadingStage] = useState(false)
  const [answers, setAnswers] = useState([])
  const { questions, loading, error, sending, bonus } = quiz

  let firstCall = false

  const handleLoading = (newState) => {
    setLoadingStage(newState)
  }

  useEffect(() => {
    validateStageOnboarding(token, history, roles.QUIZ, handleLoading)
  }, [])

  useEffect(() => {
    if (questions?.length === 0 && !firstCall && !bonus) {
      dispatch(fetchQuestions({ token }))
      firstCall = true
    }
  }, [questions])

  useEffect(() => {
    if (questionIndex === 0) {
      setAnswers([])
    }

    if (questions?.length > 0) {
      setCurrentQuestion(questions[questionIndex])
      setShowQuestion(true)
    }
  }, [questions, questionIndex])

  async function handleSubmit(items) {
    dispatch(getBonus())
    dispatch(submitAnswers({ items, token }))
  }

  async function handleTimeout() {
    const { data, status } = await get({
      url: `/onboarding/quiz/timeout`,
      token,
      source: URL_VARIABLES.ANTIFRAUDE,
    })

    if (status === 200 && data.finished && data.approved) {
      history.replace(`/result`)
    } else {
      history.replace(`/quiz-fail`)
    }
  }

  async function handleTimerEnded() {
    if (!sending) {
      handleTimeout()
    }
    return null
  }

  function handleAnswer(answer) {
    setAnswers([...answers, answer])
    const totalQuestions = questions.length - 1
    if (answers.length < totalQuestions) {
      setShowQuestion(false)
      setQuestionIndex(questionIndex + 1)
    } else if (answers.length === totalQuestions) {
      setQuestionIndex(0)
      handleSubmit([...answers, answer])
    } else if (answers.length > totalQuestions) {
      handleSubmit([answer])
    }
  }

  return (
    <Container
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
    >
      <OnboardTitle>Quiz</OnboardTitle>

      {loading && (
        <div className="loading">
          <CircularProgress />
        </div>
      )}

      {!loading && error && <div className="error">{error}</div>}

      {!loading && questions?.length > 0 && showQuestion && (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="quiz_container">
            <div className="quiz_timer">
              <CountdownTimer
                time={moment().add(50, 's')}
                callback={() => handleTimerEnded()}
              />
            </div>

            <p className="dont-break-out">
              {currentQuestion && currentQuestion.title}
            </p>
            <div className="answers">
              {currentQuestion &&
                currentQuestion.answers.map((item) => {
                  const data = addKeyToObject({ answer: item })
                  return (
                    <button
                      key={data.key}
                      type="button"
                      onClick={() => handleAnswer(data.answer)}
                    >
                      {item}
                    </button>
                  )
                })}
            </div>
          </div>
        </motion.div>
      )}

      {sending && (
        <div className="sending">
          <div className="sending__container">
            <CircularProgress />
          </div>
        </div>
      )}

      {loadingStage && (
        <div className="loading">
          <div className="loading__container">
            <CircularProgress />
          </div>
        </div>
      )}
    </Container>
  )
}
